import { useCallback, useState } from "react";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getMaxWidth = (minWidth, maxWidth) => {
  if (typeof maxWidth === "number") {
    return Math.max(maxWidth, minWidth);
  }

  const viewportBased =
    typeof window !== "undefined"
      ? Math.min(900, window.innerWidth * 0.8)
      : 900;

  return Math.max(viewportBased, minWidth);
};

/**
 * Manages the width of a resizable sidebar, persisting the value to
 * localStorage under `storageKey`. The width never drops below `minWidth`
 * (the sidebar's original fixed width) and never exceeds `maxWidth`
 * (defaults to a sensible cap based on the viewport).
 */
export default function useSidebarWidth({ storageKey, minWidth, maxWidth }) {
  const [width, setWidthState] = useState(() => {
    const max = getMaxWidth(minWidth, maxWidth);

    try {
      const stored = window.localStorage.getItem(storageKey);
      const parsed = stored != null ? parseInt(stored, 10) : NaN;
      if (!Number.isNaN(parsed)) {
        return clamp(parsed, minWidth, max);
      }
    } catch (error) {
      // Ignore (private mode / storage disabled) and fall back to the minimum.
    }

    return minWidth;
  });

  const setWidth = useCallback(
    (nextWidth) => {
      const max = getMaxWidth(minWidth, maxWidth);
      const clamped = clamp(Math.round(nextWidth), minWidth, max);

      setWidthState(clamped);

      try {
        window.localStorage.setItem(storageKey, String(clamped));
      } catch (error) {
        // Ignore storage write failures.
      }
    },
    [storageKey, minWidth, maxWidth],
  );

  return { width, setWidth };
}
